import React, { memo, useCallback, useMemo } from "react";

import {
  DrawerBody,
  DrawerHeader,
  DrawerContent,
  DrawerCloseButton,
  Center,
  Text,
  Button,
  Stack,
  Divider,
} from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import {
  RelatedMessageThreadsQuery,
  RelatedMessageThreadsQueryVariables,
} from "gql/graphql";
import RelatedMessageThread from "modules/messaging/_shared/related-message-thread";
import { omit } from "lodash";
import { useRouter } from "next/router";
import RELATED_MESSAGE_THREADS_QUERY from "modules/messaging/_networking/related-message-threads";
import { useOverlay } from "modules/providers/overlay-provider";
import {
  MESSAGE_ID_QUERY_STRING,
  MESSAGE_THREAD_ID_QUERY_STRING,
} from "modules/hooks/use-query-string";

interface IProps {
  messageThreadId: string;
}

const RelatedMessageThreadsDrawer = ({ messageThreadId }: IProps) => {
  const { pathname, query, push } = useRouter();
  const { closeDrawer } = useOverlay();

  const { data, error } = useQuery<
    RelatedMessageThreadsQuery,
    RelatedMessageThreadsQueryVariables
  >(RELATED_MESSAGE_THREADS_QUERY, {
    variables: {
      id: messageThreadId,
    },
  });

  const messageThread = useMemo(() => data?.messageThreads[0], [data]);
  const relatedMessageThreads = messageThread?.relatedMessageThreadList;

  const onMessageThreadClick = useCallback(
    (messageThreadId: string) => () => {
      closeDrawer();
      push({
        pathname,
        query: {
          ...omit(query, MESSAGE_ID_QUERY_STRING),
          [MESSAGE_THREAD_ID_QUERY_STRING]: messageThreadId,
        },
      });
    },
    [closeDrawer, pathname, push, query],
  );

  if (!messageThread || !relatedMessageThreads) return null;

  if (error) {
    return <Text>{error.message}</Text>;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton right={0} left={1} top={3} size="lg" />
      <DrawerHeader pb={0}>
        <Center
          fontWeight="700"
          fontSize={20}
          fontFamily="Outfit"
        >{`Forwards (${relatedMessageThreads.length})`}</Center>
      </DrawerHeader>
      <DrawerBody p={4}>
        <Divider />
        <Stack spacing={0} divider={<Divider />}>
          {relatedMessageThreads.map((relatedMessageThread) => (
            <Button
              variant="link"
              noOfLines={1}
              key={relatedMessageThread.id}
              h="48px"
              onClick={onMessageThreadClick(relatedMessageThread.id)}
            >
              <RelatedMessageThread
                key={relatedMessageThread.id}
                relatedMessageThread={relatedMessageThread}
              />
            </Button>
          ))}
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(RelatedMessageThreadsDrawer);
